import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Subject, takeUntil } from "rxjs";
import { FeatureType, IShapFeature } from "../../interfaces/predict.interface";

export interface IchartData {
  id: string;
  /** Shown on the axis */
  label: string;
  /** Shown in the middle of the tooltip's sentence, for a feature or the other features */
  parameter?: string;
  value: number;
}

/** Explanation of one of the features weighing the most in the prediction */
export interface IShapCaption {
  text: string;
  /** Only for a gene, to learn more about it */
  geneCardsUrl?: string;
  uniprotUrl?: string;
}

/**
 * Categorical clinical features, one-hot encoded by the model's pipeline into one feature per category
 * (e.g. "cat__location_C18.9"), which the server then wrongly types as a gene.
 */
const CATEGORICAL_CLINICAL_FEATURES = [
  "sex",
  "cancer_type",
  "location",
  "histology",
  "status_T",
  "status_N",
  "status_M",
  "patho_stage",
  "radiotherapy",
  "neoadjuvant",
  "msi_status",
];

/** One category of a one-hot encoded clinical feature */
interface IClinicalCategory {
  /** The clinical feature, e.g. "location" */
  name: string;
  /** Its category, e.g. "C18.9" */
  category: string;
}

@Component({
  selector: "app-shap-details",
  templateUrl: "./shap-details.component.html",
  styleUrls: ["./shap-details.component.scss"],
})
export class ShapDetailsComponent implements OnInit, OnDestroy {
  @Input() baseValue!: number;
  @Input() shapValues!: Record<string, IShapFeature>;

  chartData: IchartData[] = [];
  captions: IShapCaption[] = [];

  /** Emits when the component is destroyed, used to complete all active subscriptions */
  private onDestroy$ = new Subject<void>();

  constructor(public translateService: TranslateService) {}

  /**
   * Loads the chart
   */
  ngOnInit(): void {
    this.loadChart();

    this.translateService.onLangChange.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.loadChart();
    });
  }

  /**
   * Emits onDestroy$ to terminate all active subscriptions.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Splits a one-hot encoded clinical feature into the clinical feature and its category.
   * @param key The feature name, prefixed by the model's pipeline (e.g. "cat__status_T_T4a")
   * @return The clinical feature and its category (e.g. "status_T" and "T4a"), or undefined for any other feature
   */
  private clinicalCategory(key: string): IClinicalCategory | undefined {
    if (!key.startsWith("cat__")) {
      return undefined;
    }
    const encodedName = key.slice("cat__".length);
    const name = CATEGORICAL_CLINICAL_FEATURES.find((clinicalFeature) => encodedName.startsWith(`${clinicalFeature}_`));
    return name ? { name, category: encodedName.slice(name.length + 1) } : undefined;
  }

  /**
   * Type of a SHAP feature, correcting the one-hot encoded clinical features the server types as genes.
   * @param key The feature name, possibly prefixed by the model's pipeline (e.g. "num__age")
   * @param feature The SHAP feature
   * @return The feature type
   */
  private featureType(key: string, feature: IShapFeature): FeatureType {
    return this.clinicalCategory(key) ? FeatureType.CLINICAL : feature.feature_type;
  }

  /**
   * Translated label of a clinical feature.
   * @param name The clinical feature, without the model's pipeline prefix (e.g. "age")
   * @return The translated clinical parameter, or its name when not translated yet
   */
  private clinicalLabel(name: string): string {
    const translationKey = `metadataPage.prediction.shap.featureLabel.${name}`;
    const label = this.translateService.instant(translationKey);
    // A clinical feature the model added without its translation yet
    return label === translationKey ? name : label;
  }

  /**
   * Translated label of a SHAP feature, depending on its type.
   * @param key The feature name, possibly prefixed by the model's pipeline (e.g. "num__age")
   * @param feature The SHAP feature
   * @return The translated clinical parameter ("<parameter>: <category>" for a one-hot encoded one),
   * "<gene> mutation" for a mutated gene, or the gene name
   */
  private featureLabel(key: string, feature: IShapFeature): string {
    const name = key.split("__").pop() as string;
    switch (this.featureType(key, feature)) {
      case FeatureType.CLINICAL: {
        const clinicalCategory = this.clinicalCategory(key);
        return clinicalCategory ? `${this.clinicalLabel(clinicalCategory.name)}: ${clinicalCategory.category}` : this.clinicalLabel(name);
      }
      case FeatureType.GENE_MUTATED:
        return this.translateService.instant("metadataPage.prediction.shap.featureLabel.mutated", {
          gene: name.replace(/_mutated$/, ""),
        });
      default:
        return name;
    }
  }

  /**
   * Explanation of a feature's contribution to the prediction, with links to learn more about a gene.
   * @param key The feature name, possibly prefixed by the model's pipeline (e.g. "num__age")
   * @param feature The SHAP feature
   * @return The caption of the feature
   */
  private featureCaption(key: string, feature: IShapFeature): IShapCaption {
    const direction = this.translateService.instant(
      feature.value < 0 ? "metadataPage.prediction.shap.caption.negatively" : "metadataPage.prediction.shap.caption.positively"
    );
    const value = Math.round(Math.abs(feature.value) * 100 * 100) / 100;

    if (this.featureType(key, feature) === FeatureType.GENE) {
      const gene = key.split("__").pop() as string;
      return {
        text: this.translateService.instant("metadataPage.prediction.shap.caption.gene", { gene, direction, value }),
        geneCardsUrl: `https://www.genecards.org/cgi-bin/carddisp.pl?gene=${encodeURIComponent(gene)}`,
        uniprotUrl: `https://www.uniprot.org/uniprotkb?query=${encodeURIComponent(`gene:${gene} AND organism_id:9606`)}`,
      };
    }
    return {
      text: this.translateService.instant("metadataPage.prediction.shap.caption.feature", {
        feature: this.featureLabel(key, feature),
        direction,
        value,
      }),
    };
  }

  /**
   * Builds SHAP chart data with a 3% threshold, minimum 10 features, and aggregated "Others".
   */
  private loadChart(): void {
    const MIN_PERCENT = 0.03;
    const MIN_FEATURES = 5;

    const shapData: IchartData[] = Object.entries(this.shapValues).map(([key, feature]) => {
      const parameter = this.featureLabel(key, feature);
      return {
        id: key,
        label: parameter.charAt(0).toUpperCase() + parameter.slice(1),
        parameter: parameter,
        value: feature.value,
      };
    });

    shapData.sort((a, b) => Math.abs(b.value) - Math.abs(a.value));

    const CAPTIONED_FEATURES = 3;
    this.captions = shapData.slice(0, CAPTIONED_FEATURES).map((item) => this.featureCaption(item.id, this.shapValues[item.id]));

    let keptFeatures: IchartData[];

    const aboveThreshold = shapData.filter((item) => Math.abs(item.value) >= MIN_PERCENT);

    if (aboveThreshold.length >= MIN_FEATURES) {
      keptFeatures = aboveThreshold;
    } else {
      keptFeatures = shapData.slice(0, MIN_FEATURES);
    }

    const keptIds = new Set(keptFeatures.map((x) => x.id));
    const others = shapData.filter((x) => !keptIds.has(x.id));
    const othersSum = others.reduce((sum, item) => sum + item.value, 0);
    if (others.length > 0) {
      const othersLabel = this.translateService.instant("metadataPage.prediction.shap.others");
      keptFeatures.push({
        id: "others",
        label: othersLabel,
        parameter: othersLabel,
        value: othersSum,
      });
    }

    this.chartData = [
      {
        id: "baseValue",
        label: this.translateService.instant("metadataPage.prediction.shap.baseValue"),
        value: this.baseValue,
      },
      ...keptFeatures,
      {
        id: "output",
        label: this.translateService.instant("metadataPage.prediction.shap.output"),
        value: 0,
      },
    ];
  }
}
